import { LegalBasisSelectorViewModel } from "../../processingActivity/legalBasesStep/LegalBasesStep.types";
import { SelectableColoredItem, SelectableDocumentItem, SelectableItem, SelectableTransferBasisItem } from "../../processingActivity/ProcessingActivity.types";
import { SelectablePolicyItem } from "../../policy/policies/Policies.types";
import { CountryViewModel } from "./useCountryList";
import { SelectableAreaItem } from "../../annualWheel/AnnualWheel.types";
import { SelectableCompany } from "../components/dotLegalCompanySelector/DotLegalCompanySelector.types";
import { SelectableDocumentTypeItem } from "../../legalEntity/legalEntityDocumentTab/LegalEntityDocumentTab.types";
import { LegalEntityTypeSelectableItem } from "../../legalEntity/LegalEntity.types";
import { DocumentTypeUsageType } from "../../masterData/documentType/documentTypes/DocumentTypes.types";
import { apiUrls } from "../api/apiUrls.ts";
import {
    SelectableEntityTemplate,
    SelectableInputField,
    SelectableStandardEntityTemplate,
    TemplateEntityType,
} from "../../entityTemplates/EntityTemplate.types.ts";

import { useDotLegalQuery, useDotLegalQueryArray, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { ContractInputFieldsMapping, SystemInputFieldsMapping } from "@common/components/filterPopover/FilterPopover.types.ts";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";

export const selectableDeletionPeriodsUrl = "/deletion-periods/selectable";
export function useSelectableDeletionPeriodsQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("deletionperiods", selectableDeletionPeriodsUrl, {
        enabled: isEnabled,
    });
}

export function useSelectableAllLegalBasesAllQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<LegalBasisSelectorViewModel>("legalbasis", "/Legalbasis/getall/", {
        enabled: isEnabled,
    });
}

export function useSelectableLegalBasesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableLegalbases", "/Legalbasis/getSelectableLegalBases/", {
        enabled: isEnabled,
    });
}

export function useSelectablePurposesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("purposes", "/purposes/selectablePurposes", {
        enabled: isEnabled,
    });
}

export const selectableProcessingActivitiesUrl = "/processingactivity/selectableItems";
export function useSelectableProcessingActivitiesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableProcessingActivities", selectableProcessingActivitiesUrl, {
        enabled: isEnabled,
    });
}

export const selectableSystemsUrl = "/system/selectableItems";
export function useSelectableSystemsQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableSystems", selectableSystemsUrl, {
        enabled: isEnabled,
    });
}

export function useSelectableLegalEntityTypesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<LegalEntityTypeSelectableItem>>("selectableLegalEntityTypes", "/legal-entity-types/selectable-items", {
        enabled: isEnabled,
    });
}

export const getSelectableBusinessAreasUrl = (includeInactive: boolean = false) => {
    return `/businessAreas/businessareas?includeInactive=${includeInactive}`;
};

export function useSelectableBusinessAreaQuery(isEnabled: boolean = true) {
    const url = getSelectableBusinessAreasUrl();
    return useDotLegalQuery<Array<SelectableItem>>(url, {
        enabled: isEnabled,
    });
}

export function useSelectableBusinessAreaByUserAccessQuery(isEnabled: boolean = true) {
    const url = "/businessAreas/accessible";
    return useDotLegalQuery<Array<SelectableItem>>(url, {
        enabled: isEnabled,
    });
}

export function useSelectableUsersQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableUsers", "/users/responsibles", {
        enabled: isEnabled,
    });
}

export function useAllSelectableUsersQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("allSelectableUsers", "/users/allUsers", {
        enabled: isEnabled,
    });
}

export enum SelectableUserType {
    Internal = 1,
    External = 2,
}

export interface SelectableInternalOrExternalUser {
    id: string;
    name: string;
    email: string;
    type: SelectableUserType;
    active: boolean;
    businessAreas: Array<SelectableColoredItem>;
}

export const selectableInternalAndExternalUsersQueryKey = "selectableInternalAndExternalUsers";
export function useSelectableInternalAndExternalUsersQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableInternalOrExternalUser>>(selectableInternalAndExternalUsersQueryKey, "/users/internal-and-external-users", {
        enabled: isEnabled,
    });
}

export const selectableDataCategoriesUrl = "/DataCategories/selectable";
export function useDataCategoriesQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(selectableDataCategoriesUrl, {
        enabled: isEnabled,
    });
}

export const selectableDataSubjectsUrl = "/DataSubjects/selectable";
export function useDataSubjectsQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(selectableDataSubjectsUrl, {
        enabled: isEnabled,
    });
}

export const selectableIncidentTypesUrl = "/incidentTypes/selectableItems";
export function useSelectableIncidentTypesQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(selectableIncidentTypesUrl, {
        enabled: isEnabled,
    });
}

const selectableIncidentAffectedServicesUrl = "/incident-affected-services/selectable-items";
export function useSelectableIncidentAffectedServicesQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(selectableIncidentAffectedServicesUrl, {
        enabled: isEnabled,
    });
}

export function useNationalLawsQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableNationalLaws", "/legalbasis/selectableNationalLaws", {
        enabled: isEnabled,
    });
}

export const selectableTransferBasesUrl = "/legalbasis/getTransferBasis";

export function useTransferBasesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableTransferBasisItem>>("selectableTransferBases", selectableTransferBasesUrl, {
        enabled: isEnabled,
    });
}

export const getSecurityMeasures = (showAllSecurityMeasures: boolean) =>
    `/SecurityMeasures/selectableSecurityMeasures?showAllSecurityMeasures=${showAllSecurityMeasures}`;
export function useSecurityMeasuresQuery(isEnabled: boolean = true, showAllSecurityMeasures: boolean) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>(
        "selectableSecurityMeasures",
        `/SecurityMeasures/selectableSecurityMeasures?showAllSecurityMeasures=${showAllSecurityMeasures}`,
        {
            enabled: isEnabled,
        }
    );
}

export const selectableLegalEntitiesUrl = "/LegalEntity/selectableentities";
export function useCompaniesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableCompanies", selectableLegalEntitiesUrl, {
        enabled: isEnabled,
    });
}

export function useGroupCompaniesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableGroupCompanies", apiUrls().legalEntities.groupEntities, {
        enabled: isEnabled,
    });
}

export function useSelectableLegalEntitiesByAccessQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectable-by-access", "/LegalEntity/selectable-by-access", {
        enabled: isEnabled,
    });
}

export const selectableGroupEntitiesForUserUrl = "/legal-entities/selectableGroupEntitiesForUser";
export function useGroupEntitiesForUserQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableCompany>>("selectableGroupEntitiesForUser", selectableGroupEntitiesForUserUrl, {
        enabled: isEnabled,
    });
}

export function useCountriesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<CountryViewModel>>("countries", "/Countries", {
        enabled: isEnabled,
    });
}

export function useDocumentQuery(isEnabled: boolean = true, usageType?: DocumentTypeUsageType) {
    return useDotLegalQueryWithKey<Array<SelectableDocumentItem>>(
        "selectableDocumentsByUsageType" + usageType,
        "/LegalEntity/selectableDocumentsByUsageType?documentTypeUsage=" + usageType,
        {
            enabled: isEnabled,
        }
    );
}

export function getSelectableProcessingActivitiesByAccessUrl(onlyUserSpecific: boolean) {
    return `/processingactivity/selectableItemsByAccess?onlyuserspecific=${onlyUserSpecific}`;
}

export function useProcessingActivityQuery(onlyUserSpecific: boolean) {
    return useDotLegalQuery<Array<SelectableItem>>(getSelectableProcessingActivitiesByAccessUrl(onlyUserSpecific));
}

export const selectablePoliciesUrl = "/policies/selectablePolicies";
export function usePoliciesQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectablePolicyItem>>("selectablePolicies", selectablePoliciesUrl, {
        enabled: isEnabled,
    });
}

export function useSystemDocumentQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectablePolicyItem>>("selectableSystemDocuments", "/system/getSelectableDocuments", {
        enabled: isEnabled,
    });
}

export const selectableCertificationsUrl = "/certifications/selectableItems";
export function useCertificationsQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableCertifications", selectableCertificationsUrl, {
        enabled: isEnabled,
    });
}

export function useComplianceAreaQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableAreaItem>>("selectableAreas", "/areas/selectableitems", {
        enabled: isEnabled,
    });
}

export const selectableTagsUrl = "/tag/selectableTags";
export function useSelectableTagsQuery(isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableColoredItem>>("selectableTags", selectableTagsUrl, {
        enabled: isEnabled,
    });
}

export function getSelectableDocumentTypesUrl(usageType: DocumentTypeUsageType) {
    return `/documentTypes/selectableItems?usageType=${usageType}`;
}

export function useSelectableDocumentTypesQueryByUsageType(usageType: DocumentTypeUsageType, isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableDocumentTypeItem>>("selectableDocumentTypes" + usageType, getSelectableDocumentTypesUrl(usageType), {
        enabled: isEnabled,
    });
}

export const selectableContractTypesUrl = "/contractTypes/selectableItems";
export function useSelectableContractTypes(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(selectableContractTypesUrl, {
        enabled: isEnabled,
    });
}

export const paymentTermsUrl = "/paymentTerms/selectableItems";
export function useSelectablePaymentTerms(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(paymentTermsUrl, {
        enabled: isEnabled,
    });
}

export function useSelectableFrameworkCategories(frameworkId: string, isEnabled: boolean = true) {
    return useDotLegalQueryWithKey<Array<SelectableItem>>("selectableFrameworkCategories", `/frameworks/${frameworkId}/selectable`, {
        enabled: isEnabled,
    });
}

export function getSelectableEntityTemplatesUrl(templateEntityType: TemplateEntityType, includeInactive: boolean = false) {
    return `/entity-templates/selectable-templates?includeInactive=${includeInactive}&templateEntityType=${templateEntityType}`;
}

export function useSelectableEntityTemplates(templateEntityType: TemplateEntityType, isEnabled: boolean = true) {
    return useDotLegalQueryArray<Array<DotLegalSelectOption>>(
        ["selectableEntityTemplates", templateEntityType],
        getSelectableEntityTemplatesUrl(templateEntityType),
        {
            enabled: isEnabled,
        }
    );
}

export function useAccessibleEntityTemplates(templateEntityType: TemplateEntityType, isEnabled: boolean = true) {
    return useDotLegalQueryArray<Array<DotLegalSelectOption>>(
        ["accessibleEntityTemplates", templateEntityType],
        `/entity-templates/accessible-templates?templateEntityType=${templateEntityType}`,
        {
            enabled: isEnabled,
        }
    );
}

export function useSelectableStandardEntityTemplates(templateEntityType: TemplateEntityType, isEnabled: boolean = true) {
    return useDotLegalQueryArray<Array<SelectableStandardEntityTemplate>>(
        ["selectableStandardEntityTemplates", templateEntityType],
        `/entity-templates/selectable-standard-templates?templateEntityType=${templateEntityType}`,
        {
            enabled: isEnabled,
        }
    );
}

export function useSelectableEntityTemplatesForCreation(templateEntityType: TemplateEntityType, includeObligations: boolean, isEnabled: boolean = true) {
    return useDotLegalQueryArray<Array<SelectableEntityTemplate>>(
        ["selectableEntityTemplatesForCreation", templateEntityType, includeObligations],
        `/entity-templates/for-creation?templateEntityType=${templateEntityType}&includeObligations=${includeObligations}`,
        {
            enabled: isEnabled,
        }
    );
}

export function useSelectableInputFields(templateEntityType?: TemplateEntityType, isEnabled: boolean = true) {
    const customInputFieldsUrl = `/settings/input-fields/selectable?templateEntityType=${templateEntityType}`;
    return useDotLegalQuery<Array<SelectableInputField>>(customInputFieldsUrl, {
        enabled: isEnabled,
    });
}

export function useContractInputFieldsMapping(isEnabled: boolean = true) {
    const url = `/contracts/input-fields-mapping`;
    return useDotLegalQuery<ContractInputFieldsMapping>(url, {
        enabled: isEnabled,
    });
}

export function useSystemInputFieldsMapping(isEnabled: boolean = true) {
    const url = `/System/input-fields-mapping`;
    return useDotLegalQuery<SystemInputFieldsMapping>(url, {
        enabled: isEnabled,
    });
}

export function useGetContractCustomInputFields(isEnabled: boolean = true) {
    let { isLoading, data } = useContractInputFieldsMapping(isEnabled);
    return { isLoading, customFieldsSelectableValues: data?.customFieldsSelectableValues };
}

export function useGetContractFieldsAndGroupings(isEnabled: boolean = true) {
    let { isLoading, data } = useContractInputFieldsMapping(isEnabled);
    return { isLoading, contractFieldsAndGroupings: data?.contractFieldsAndGroupings };
}

// Fetched for a filter's fixedData rather than its dataUrl so the "AreaName - Level" chip resolves its own label;
// the shared /filter-data chip resolver only understands entity ids, not the "{riskAreaId}:{level}" pairs.
export function useSelectableRiskAreaLevelsQuery(isEnabled: boolean) {
    return useDotLegalQuery<Array<{ id: string; name: string }>>("/risk-areas/selectable-risk-levels", { enabled: isEnabled });
}

export function useRiskAreas() {
    const { isLoading, data } = useDotLegalQuery<Array<SelectableItem>>("/risk-areas/selectable");

    return { isLoading, riskAreas: data };
}

// Both of these proxy through to the separate questionnaire service. Callers that need them to populate a
// filter or a picker on page load must not inherit the global throwOnError: a questionnaire service that is
// down would otherwise take the hosting page down with it, so the list degrades to empty instead.
export function useSelectableAuditQuestionnairesQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(apiUrls().questionnaires.selectableItems, {
        enabled: isEnabled,
        throwOnError: false,
        retry: false,
    });
}

export function useSelectableVendorClassificationsQuery(isEnabled: boolean = true) {
    return useDotLegalQuery<Array<SelectableItem>>(apiUrls().vendorClassifications.selectableItems, {
        enabled: isEnabled,
        throwOnError: false,
        retry: false,
    });
}

// The custom fields the customer's legal entity templates define. Only the fields themselves - unlike the system
// and contract mappings there is no accompanying dictionary of built-in fields, because the legal entity
// overview's own column vocabulary is static and already lives in the client.
export function useLegalEntityCustomInputFields(isEnabled: boolean = true) {
    return useSelectableInputFields(TemplateEntityType.LegalEntity, isEnabled);
}
